import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, NavLink, Link, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ConfigProvider, useConfig } from "./context/ConfigContext";
import { loadRecaptcha } from "./utils/recaptcha";
import UserMenu from "./components/UserMenu";
// Eager: the public landing page, the postcard-scan page, and 404 must paint
// on the first commit (the landing replaces the static #root markup from
// public/index.html, so it can't wait on a chunk download).
import Landing from "./pages/Landing";
import PublicInterest from "./pages/PublicInterest";
import NotFound from "./pages/NotFound";

// Lazy: everything behind the app shell. Keeps deck.gl/maplibre, ag-grid,
// MSAL and Google OAuth out of the main chunk (SEO plan — code splitting).
const OAuthProviders = lazy(() => import("./components/OAuthProviders"));
const Home = lazy(() => import("./pages/Home"));
const MapExplorer = lazy(() => import("./pages/MapExplorer"));
const Recruitment = lazy(() => import("./pages/Recruitment"));
const Scheduling = lazy(() => import("./pages/Scheduling"));
const Postcards = lazy(() => import("./pages/Postcards"));
const PostcardSend = lazy(() => import("./pages/PostcardSend"));
const PostcardConfirm = lazy(() => import("./pages/PostcardConfirm"));
const PostcardReceipt = lazy(() => import("./pages/PostcardReceipt"));
const PostcardROI = lazy(() => import("./pages/PostcardROI"));
const PostcardResponses = lazy(() => import("./pages/PostcardResponses"));
const Admin = lazy(() => import("./pages/Admin"));
const Account = lazy(() => import("./pages/Account"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
import brandMark from "./assets/logos/g-tower-waves.svg";

const DEFAULT_FILTERS = {
  state: "",
  classes: "Technician,General,Amateur Extra,Advanced",
  expireAfter: new Date().toISOString().slice(0, 10),
};

// Full marketing title from public/index.html, captured before any route
// changes it. The landing page (/) keeps it; in-app pages get a short one.
const LANDING_TITLE = document.title;

// Per-route titles (SEO plan Phase 1f) — better tab labels and GA4 page
// reports. First match wins, so list more specific paths first. Anything
// unmatched is the NotFound page.
const PAGE_TITLES = [
  [/^\/home\/?$/, "Home"],
  [/^\/map\/?$/, "Map"],
  [/^\/recruitment\/?$/, "Recruitment"],
  [/^\/scheduling\/?$/, "Scheduling"],
  [/^\/postcards\/send\/confirm\/?$/, "Confirm Postcards"],
  [/^\/postcards\/send\/receipt\/?$/, "Postcard Receipt"],
  [/^\/postcards\/send\/?$/, "Send Postcards"],
  [/^\/postcards\/roi\/?$/, "Postcard ROI"],
  [/^\/postcards\/responses\/[^/]+\/?$/, "Postcard Responses"],
  [/^\/postcards\/?$/, "Postcards"],
  [/^\/admin(\/[^/]+)?\/?$/, "Admin"],
  [/^\/account\/?$/, "Account"],
  [/^\/reset-password\/?$/, "Reset Password"],
  [/^\/interested(\/[^/]+)?\/?$/, "Welcome"],
];

function usePageTitle(pathname) {
  useEffect(() => {
    if (pathname === "/") {
      document.title = LANDING_TITLE;
      return;
    }
    const match = PAGE_TITLES.find(([pattern]) => pattern.test(pathname));
    document.title = `${match ? match[1] : "Page Not Found"} — Ham Recruiter`;
  }, [pathname]);
}

/**
 * RecruitmentGuard — gate the Recruitment page behind auth.
 *
 * Per the landing-page spec (§1 routing table), unauthenticated users hitting
 * `/recruitment` get bounced to `/scheduling?mode=register&redirect=/recruitment`
 * so they land back here after sign-up/sign-in. Authenticated users render
 * the page directly.
 */
function RecruitmentGuard({ children, redirect = "/recruitment" }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    // Don't redirect mid-session-check — wait for /api/auth/me to resolve.
    return null;
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={`/scheduling?mode=register&redirect=${redirect}`}
        replace
      />
    );
  }

  return children;
}

/** Suspense fallback / config wait — the same thin top bar the pages use. */
function PageLoading() {
  return (
    <div className="page-loading" role="status" aria-label="Loading">
      <div className="loading-bar">
        <div className="loading-bar__progress" />
      </div>
    </div>
  );
}

function HomeGuard({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    // Optimistically render Home while /api/auth/me is in flight, as long as a
    // token exists in localStorage. Home's dashboard fetch then runs in parallel
    // with /api/auth/me instead of waiting for it — saves one round-trip.
    // authedFetch handles 401/refresh if the token turns out to be invalid, and
    // the next render (after loading clears) will Navigate away if auth failed.
    if (localStorage.getItem("access_token")) return children;
    return null;
  }
  if (!isAuthenticated) {
    return <Navigate to="/scheduling?mode=register&redirect=/home" replace />;
  }
  return children;
}

/** In-app (app-shell) routes. Rendered only after public config loads. */
function AppRoutes({ stats }) {
  // Postcard routes exist only when the POSTCARDS_ENABLED runtime flag is on;
  // otherwise /postcards* falls through to the "*" NotFound route.
  const { postcardsEnabled } = useConfig();
  return (
    <Routes>
      <Route
        path="/home"
        element={
          <HomeGuard>
            <Home />
          </HomeGuard>
        }
      />
      <Route
        path="/map"
        element={
          <RecruitmentGuard redirect="/map">
            <MapExplorer filters={DEFAULT_FILTERS} stats={stats} />
          </RecruitmentGuard>
        }
      />
      <Route
        path="/recruitment"
        element={
          <RecruitmentGuard>
            <Recruitment filters={DEFAULT_FILTERS} />
          </RecruitmentGuard>
        }
      />
      <Route path="/scheduling" element={<Scheduling />} />
      {postcardsEnabled && (
        <>
          <Route path="/postcards" element={<Postcards />} />
          <Route path="/postcards/send" element={<PostcardSend />} />
          <Route path="/postcards/send/confirm" element={<PostcardConfirm />} />
          <Route path="/postcards/send/receipt" element={<PostcardReceipt />} />
          <Route path="/postcards/roi" element={<PostcardROI />} />
          <Route path="/postcards/responses/:batchId" element={<PostcardResponses />} />
        </>
      )}
      <Route path="/admin" element={<Admin />} />
      <Route path="/admin/:tab" element={<Admin />} />
      <Route path="/account" element={<Account />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

/**
 * AppContent — inner component that has access to AuthContext.
 * Renders the header, nav, user menu, stats, and routes.
 */
function AppContent({ configLoaded, googleClientId, microsoftClientId }) {
  const { user, loading: authLoading, authedFetch } = useAuth();
  const { postcardsEnabled } = useConfig();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // While the initial /api/auth/me round-trip is in flight, treat the user as
  // unauthenticated for nav-visibility purposes. Prevents R+S from briefly
  // flashing on first paint when there's no token, or when the token is being
  // validated.
  const isAuthed = !authLoading && !!user;

  usePageTitle(location.pathname);

  // /api/stats is auth-gated per security plan item #1, so it only resolves for
  // logged-in users. The header no longer shows any stats; `stats` is passed to
  // MapExplorer purely to populate the (now login-only) map legend counts. For
  // anonymous users this silently 401s and `stats` stays null.
  const fetchStats = useCallback(async () => {
    try {
      const res = await authedFetch("/api/stats");
      if (!res.ok) return;
      setStats(await res.json());
    } catch {
      // Silent — map legend just omits the counts.
    }
  }, [authedFetch]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Close the mobile drawer whenever the route changes (covers programmatic
  // navigation as well as link taps).
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Nav links shared by the desktop inline nav and the mobile drawer. Admin is
  // role-gated; the rest require auth. Keeps the two renderings in sync.
  const navLinks = [
    isAuthed && { to: "/home", label: "Home" },
    isAuthed && { to: "/map", label: "Map" },
    isAuthed && { to: "/recruitment", label: "Recruitment" },
    isAuthed && { to: "/scheduling", label: "Scheduling" },
    isAuthed && postcardsEnabled && { to: "/postcards", label: "Postcards" },
    user?.role === "admin" && { to: "/admin", label: "Admin" },
  ].filter(Boolean);

  // Landing page (/) renders OUTSIDE the standard app-shell — it owns
  // its own header, sections, and footer. Short-circuit AFTER hooks so the
  // hooks-rule is preserved when navigating between routes. It does NOT wait
  // for public config: this is the first commit that replaces the static
  // landing markup in #root, so it must not be empty.
  if (location.pathname === "/") {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
      </Routes>
    );
  }

  // Public postcard-scan landing page (Close the Loop CL-2) — no auth, no app
  // shell. A recipient scanning the QR lands here after the /r/{code} resolver
  // records the scan and redirects. Short-circuited AFTER hooks (hooks-rule).
  // Gated on the POSTCARDS_ENABLED flag, so it waits for public config: loading
  // bar until then, NotFound when postcards are off.
  if (location.pathname.startsWith("/interested")) {
    if (!configLoaded) return <PageLoading />;
    if (!postcardsEnabled) return <NotFound />;
    return (
      <Routes>
        <Route path="/interested/:code" element={<PublicInterest />} />
        <Route path="/interested" element={<PublicInterest />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    );
  }

  return (
    <div className="app-shell">
      <header className="app-header">
        <Link to={isAuthed ? "/home" : "/"} className="app-header__brand" aria-label="Ham Recruiter — home">
          <img src={brandMark} alt="" aria-hidden="true" className="app-header__logo" width="26" height="26" />
          <span className="app-header__title">Ham Recruiter</span>
          <span className="app-header__subtitle">FCC ULS</span>
        </Link>
        <nav className="app-header__nav">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `app-header__nav-link${isActive ? " app-header__nav-link--active" : ""}`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>
        <div className="app-header__meta">
          {/* Header stats removed per security plan item #1 (PO decision
              2026-06-25): /api/stats is now gated, so the "Updated …" line is
              gone. `stats` survives only to feed the map legend counts. */}
          <UserMenu />
        </div>
        <button
          type="button"
          className={`app-header__hamburger${mobileNavOpen ? " app-header__hamburger--open" : ""}`}
          aria-label={mobileNavOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={mobileNavOpen}
          onClick={() => setMobileNavOpen((prev) => !prev)}
        >
          <span className="app-header__hamburger-icon" aria-hidden="true" />
        </button>
      </header>

      {mobileNavOpen && (
        <>
          <button
            type="button"
            className="app-nav-overlay"
            aria-label="Close navigation menu"
            onClick={() => setMobileNavOpen(false)}
          />
          <nav className="app-nav-drawer">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setMobileNavOpen(false)}
                className={({ isActive }) =>
                  `app-nav-drawer__link${isActive ? " app-nav-drawer__link--active" : ""}`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
        </>
      )}

      <div className="app-content">
        {/* In-app routes wait for /api/config/public so GoogleOAuthProvider /
            MsalProvider mount once with their final client IDs (no re-init).
            The header above and the landing page (/) don't wait. */}
        {!configLoaded ? (
          <PageLoading />
        ) : (
          <Suspense fallback={<PageLoading />}>
            <OAuthProviders googleClientId={googleClientId} microsoftClientId={microsoftClientId}>
              {/* Inner boundary: page-chunk loads replace only the routes, so
                  the OAuth providers above stay mounted between pages. */}
              <Suspense fallback={<PageLoading />}>
                <AppRoutes stats={stats} />
              </Suspense>
            </OAuthProviders>
          </Suspense>
        )}
      </div>
    </div>
  );
}

/**
 * App — root component.
 *
 * Renders immediately. /api/config/public is fetched in the background:
 *   - The landing page (/) paints on the first commit, so createRoot swaps the
 *     static #root markup from public/index.html straight for the React
 *     landing (no blank frame). Its contact form waits on `loaded` for the
 *     reCAPTCHA key.
 *   - In-app routes show a loading bar until config arrives, then mount inside
 *     OAuthProviders (Google / Microsoft), which is lazy-loaded.
 */
export default function App() {
  const [googleClientId, setGoogleClientId] = useState("");
  const [microsoftClientId, setMicrosoftClientId] = useState("");
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState("");
  const [postcardsEnabled, setPostcardsEnabled] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    fetch("/api/config/public")
      .then((res) => res.json())
      .then((data) => {
        setGoogleClientId(data.google_client_id || "");
        setMicrosoftClientId(data.microsoft_client_id || "");
        setRecaptchaSiteKey(data.recaptcha_site_key || "");
        setPostcardsEnabled(!!data.postcards_enabled);
      })
      .catch(() => {
        // If config endpoint fails, continue without Google/Microsoft — email/password still works
      })
      .finally(() => setConfigLoaded(true));
  }, []);

  // Pre-load the reCAPTCHA v3 script once the site key is known (no-op when unset).
  useEffect(() => {
    if (recaptchaSiteKey) {
      loadRecaptcha(recaptchaSiteKey);
    }
  }, [recaptchaSiteKey]);

  const config = useMemo(
    () => ({
      loaded: configLoaded,
      googleEnabled: !!googleClientId,
      microsoftEnabled: !!microsoftClientId,
      recaptchaSiteKey,
      postcardsEnabled,
    }),
    [configLoaded, googleClientId, microsoftClientId, recaptchaSiteKey, postcardsEnabled]
  );

  return (
    <ConfigProvider value={config}>
      <AuthProvider>
        <BrowserRouter>
          <AppContent
            configLoaded={configLoaded}
            googleClientId={googleClientId}
            microsoftClientId={microsoftClientId}
          />
        </BrowserRouter>
      </AuthProvider>
    </ConfigProvider>
  );
}
